import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { collection, getDocs, doc, updateDoc, orderBy, query } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { toast } from 'react-toastify';
import { FaDollarSign, FaFileInvoiceDollar, FaHeartbeat, FaCog, FaExternalLinkAlt, FaFilePdf, FaCopy, FaUsers } from 'react-icons/fa';
import { apiUrl, apiUrlDev, mainApiUrl, mainApiUrlDev } from '../../globalVariables';
import { EXEMPT_REASONS, getExemptReasonLabel, isGrowthSubscription } from './enterpriseBillingOptions';
import PerLoanPricingFields, {
    BillingModeSelect,
    buildPerLoanBilling,
    perLoanFormFromBilling,
    todayDateString,
    validatePerLoanForm,
} from './PerLoanPricingFields';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// Used only until GET /admin/billing-usage/los-stage-defaults answers; the
// backend response is authoritative.
const STAGE_MATCH_FALLBACK = ['stageId', 'exact', 'contains'];

const STAGE_MATCH_HELP = {
    stageId: 'Numeric stage id (Arive)',
    exact: 'Stage name matches exactly',
    contains: 'Stage name contains this text (wildcard)',
};

/**
 * Organization fields that indicate a connected LOS, in the order we prefer to
 * auto-select them. `encompassEPC` and `encompass` both classify through the
 * Encompass strategy but stay distinct losKeys so rules can diverge.
 */
const LOS_FIELD_MAP = [
    { losKey: 'arive', field: 'arive', label: 'Arive' },
    { losKey: 'lendingpad', field: 'lendingpad', label: 'LendingPad' },
    { losKey: 'meridianlink', field: 'meridianlink', label: 'MeridianLink' },
    { losKey: 'encompass-epc', field: 'encompassEPC', label: 'Encompass (Partner Connect)' },
    { losKey: 'encompass', field: 'encompass', label: 'Encompass (REST)' },
];

/** Is this org's LOS integration actually present//enabled on the org doc? */
const isLosConnected = (orgData, field) => {
    const value = orgData?.[field];
    if (!value || typeof value !== 'object') return false;
    // Most integrations carry `enabled`; some only exist once connected.
    return value.enabled !== false;
};

const STATUS_STYLES = {
    current: { badge: 'bg-success', label: 'Current' },
    grace_period: { badge: 'bg-warning text-dark', label: 'Grace Period' },
    restricted: { badge: 'bg-danger', label: 'Restricted' },
    suspended: { badge: 'badge-dark', label: 'Suspended', customStyle: { backgroundColor: '#1a1a2e', color: '#fff' } },
};

const INVOICE_STATUS_STYLES = {
    paid: { badge: 'bg-success', label: 'Paid' },
    open: { badge: 'bg-warning text-dark', label: 'Open' },
    payment_failed: { badge: 'bg-danger', label: 'Payment Failed' },
    uncollectible: { badge: 'bg-danger', label: 'Uncollectible' },
    forgiven: { badge: 'bg-secondary', label: 'Forgiven' },
    void: { badge: 'bg-secondary', label: 'Voided' },
    draft: { badge: 'bg-info', label: 'Draft' },
};

// With `onActivate`, the dialog is the "Set to Enterprise" confirmation: it opens on the
// Configuration tab and hands the chosen billing to `onActivate` instead of saving it.
const EnterpriseBillingDialog = ({
    isOpen,
    onClose,
    organizationData,
    firestore,
    onOrganizationUpdate,
    useDevDb = false,
    onActivate,
    isActivating = false,
}) => {
    const activeMainApiUrl = useDevDb ? mainApiUrlDev : mainApiUrl;
    const activeApiUrl = useDevDb ? apiUrlDev : apiUrl;
    const [activeTab, setActiveTab] = useState('status');
    const [invoices, setInvoices] = useState([]);
    const [loadingInvoices, setLoadingInvoices] = useState(false);
    const [actionInProgress, setActionInProgress] = useState(null);

    // Configuration form state
    const [configForm, setConfigForm] = useState({
        billingType: 'per_loan',
        billingMode: 'invoice',
        // Per-user fields
        standardSeatPrice: 0,
        assistantSeatPrice: 0,
        cycle: 'month',
    });
    const [perLoanForm, setPerLoanForm] = useState(perLoanFormFromBilling());
    const [activationChoice, setActivationChoice] = useState('bill');
    const [exemptReason, setExemptReason] = useState('');
    const [exemptNote, setExemptNote] = useState('');
    const [savingConfig, setSavingConfig] = useState(false);

    // ---- Per-LOS billing stage rules ----
    // Which LOS the operator is currently editing rules for, the built-in
    // ladders fetched from the backend (single source of truth — the UI never
    // hard-codes its own copy), and the working set of rows.
    const [selectedLos, setSelectedLos] = useState('');
    const [stageDefaults, setStageDefaults] = useState(null);
    const [stageMatchTypes, setStageMatchTypes] = useState(STAGE_MATCH_FALLBACK);
    const [stageRules, setStageRules] = useState({ funded: [], dead: [] });
    const [loadingStageDefaults, setLoadingStageDefaults] = useState(false);
    const [savingStageRules, setSavingStageRules] = useState(false);
    const [backfillMonth, setBackfillMonth] = useState('');
    const [backfillSendEmails, setBackfillSendEmails] = useState(false);
    const [savingBackfill, setSavingBackfill] = useState(false);
    // Per-user checkout link state
    const [checkoutUrl, setCheckoutUrl] = useState('');
    const [generatingCheckout, setGeneratingCheckout] = useState(false);

    const billing = organizationData?.billing || {};
    const orgId = organizationData?.id;
    const isActivation = typeof onActivate === 'function';
    // Whitelist activation: the organization is created later, at sign-up.
    const isNewOrganization = !orgId;
    const canKeepGrowthBilling = isActivation && isGrowthSubscription(organizationData?.subscription);
    const billingStatus = billing.billingStatus || 'current';
    const billingExempt = billing.billingExempt === true;

    const statusUpdatedAt = billing.billingStatusUpdatedAt || 0;
    const daysInState = statusUpdatedAt ? Math.floor((Date.now() - statusUpdatedAt) / MS_PER_DAY) : 0;

    const graceDeadline = billing.graceDeadline;
    const restrictedDeadline = billing.restrictedDeadline;
    const graceRemaining = graceDeadline ? Math.max(0, Math.ceil((graceDeadline - Date.now()) / MS_PER_DAY)) : null;
    const restrictedRemaining = restrictedDeadline ? Math.max(0, Math.ceil((restrictedDeadline - Date.now()) / MS_PER_DAY)) : null;

    const hasBillingConfig = billing.pricePerLoan !== undefined && billing.basePricePerLoan !== undefined;

    const fetchInvoices = useCallback(async () => {
        if (!firestore || !orgId) return;
        setLoadingInvoices(true);
        try {
            const invoicesRef = collection(firestore, 'organizations', orgId, 'billing-invoices');
            const q = query(invoicesRef, orderBy('createdAt', 'desc'));
            const snapshot = await getDocs(q);
            const docs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            setInvoices(docs);
        } catch (err) {
            console.error('Error fetching invoices:', err);
            toast.error('Failed to load invoices');
        } finally {
            setLoadingInvoices(false);
        }
    }, [firestore, orgId]);

    useEffect(() => {
        if (isOpen) {
            fetchInvoices();
        }
    }, [isOpen, fetchInvoices]);

    // Initialize config form when dialog opens
    useEffect(() => {
        if (isOpen && billing) {
            const perUser = billing.perUserConfig || {};
            const perLoan = perLoanFormFromBilling(billing);
            setPerLoanForm(isActivation && !perLoan.billingStartDate ? { ...perLoan, billingStartDate: todayDateString() } : perLoan);
            if (isActivation) {
                setActiveTab('config');
                setActivationChoice('bill');
                setExemptReason('');
                setExemptNote('');
            }
            setConfigForm({
                billingType: billing.billingType || 'per_loan',
                billingMode: billing.perUserConfig ? billing.billingMode || 'auto_charge' : perLoan.billingMode,
                standardSeatPrice: perUser.standardSeatPrice || 0,
                assistantSeatPrice: perUser.assistantSeatPrice || 0,
                cycle: perUser.cycle || 'month',
            });
            setCheckoutUrl('');
        }
    }, [isOpen, orgId]);

    // ---- Per-LOS stage rules ----

    const savedStageRules = billing.losStageRules || {};

    /**
     * LOS the operator can edit rules for. Anything already connected on the
     * org doc comes first (and drives auto-selection); the rest stay available
     * because a loan can carry a losKey the org doc never advertised — Bat Inc
     * had loans across eight of them.
     */
    const losOptions = useMemo(() => {
        const connected = LOS_FIELD_MAP.filter((entry) =>
            isLosConnected(organizationData, entry.field),
        );
        const connectedKeys = new Set(connected.map((entry) => entry.losKey));

        return [
            ...connected.map((entry) => ({ ...entry, connected: true })),
            ...LOS_FIELD_MAP.filter((entry) => !connectedKeys.has(entry.losKey)).map(
                (entry) => ({ ...entry, connected: false }),
            ),
        ];
    }, [organizationData]);

    // Fetch the canonical ladders once per open, and auto-select the org's LOS.
    useEffect(() => {
        if (!isOpen || !orgId) return;

        let cancelled = false;
        const loadDefaults = async () => {
            setLoadingStageDefaults(true);
            try {
                const adminUserId = getAuth()?.currentUser?.uid;
                const resp = await fetch(
                    `${activeApiUrl}/admin/billing-usage/los-stage-defaults?userId=${encodeURIComponent(adminUserId || '')}`,
                );
                const data = await resp.json();
                if (cancelled) return;
                if (data?.success) {
                    setStageDefaults(data.defaults || {});
                    if (Array.isArray(data.matchTypes) && data.matchTypes.length) {
                        setStageMatchTypes(data.matchTypes);
                    }
                } else {
                    toast.error('Could not load stage defaults: ' + (data?.reason || 'unknown error'));
                }
            } catch (err) {
                if (!cancelled) toast.error('Could not load stage defaults: ' + err.message);
            } finally {
                if (!cancelled) setLoadingStageDefaults(false);
            }
        };

        loadDefaults();
        // Prefer a LOS the org already has rules for, else its first connected one.
        const configured = Object.keys(billing.losStageRules || {});
        const firstConnected = losOptions.find((entry) => entry.connected);
        setSelectedLos(configured[0] || firstConnected?.losKey || LOS_FIELD_MAP[0].losKey);

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, orgId]);

    // Load the editable rows whenever the selected LOS (or fetched defaults) change.
    useEffect(() => {
        if (!selectedLos) return;
        const saved = savedStageRules[selectedLos];
        const hasSaved =
            saved &&
            ((Array.isArray(saved.funded) && saved.funded.length > 0) ||
                (Array.isArray(saved.dead) && saved.dead.length > 0));

        if (hasSaved) {
            setStageRules({
                funded: (saved.funded || []).map((r) => ({ ...r })),
                dead: (saved.dead || []).map((r) => ({ ...r })),
            });
            return;
        }

        const fallback = stageDefaults?.[selectedLos];
        setStageRules({
            funded: (fallback?.funded || []).map((r) => ({ ...r })),
            dead: (fallback?.dead || []).map((r) => ({ ...r })),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedLos, stageDefaults, orgId]);

    const hasCustomRulesForSelected = Boolean(
        savedStageRules[selectedLos] &&
            ((savedStageRules[selectedLos].funded || []).length ||
                (savedStageRules[selectedLos].dead || []).length),
    );

    const updateStageRule = (bucket, index, patch) => {
        setStageRules((prev) => ({
            ...prev,
            [bucket]: prev[bucket].map((rule, i) => (i === index ? { ...rule, ...patch } : rule)),
        }));
    };

    const addStageRule = (bucket) => {
        setStageRules((prev) => ({
            ...prev,
            [bucket]: [...prev[bucket], { match: 'contains', value: '' }],
        }));
    };

    const removeStageRule = (bucket, index) => {
        setStageRules((prev) => ({
            ...prev,
            [bucket]: prev[bucket].filter((_, i) => i !== index),
        }));
    };

    const resetStageRulesToDefaults = () => {
        const fallback = stageDefaults?.[selectedLos];
        setStageRules({
            funded: (fallback?.funded || []).map((r) => ({ ...r })),
            dead: (fallback?.dead || []).map((r) => ({ ...r })),
        });
        toast.info('Reverted to built-in defaults — click Save to apply.');
    };

    /** Drop blank rows and coerce stageId values to numbers before persisting. */
    const normalizeRulesForSave = (rules) =>
        rules
            .filter((rule) => String(rule.value ?? '').trim() !== '')
            .map((rule) => ({
                match: rule.match,
                value: rule.match === 'stageId' ? Number(rule.value) : String(rule.value).trim(),
                ...(rule.label ? { label: rule.label } : {}),
            }))
            .filter((rule) => rule.match !== 'stageId' || Number.isFinite(rule.value));

    const handleSaveStageRules = async () => {
        if (!selectedLos) return;
        const funded = normalizeRulesForSave(stageRules.funded);
        const dead = normalizeRulesForSave(stageRules.dead);

        if (funded.length === 0) {
            toast.error('Add at least one "funded" rule — saving none would stop this LOS billing entirely.');
            return;
        }

        setSavingStageRules(true);
        await updateBillingField(
            { losStageRules: { ...savedStageRules, [selectedLos]: { funded, dead } } },
            `Stage rules saved for ${selectedLos}`,
        );
        setSavingStageRules(false);
    };

    const handleClearStageRules = async () => {
        if (!selectedLos) return;
        const next = { ...savedStageRules };
        delete next[selectedLos];

        setSavingStageRules(true);
        await updateBillingField(
            { losStageRules: next },
            `${selectedLos} reverted to built-in rules`,
        );
        setSavingStageRules(false);
    };

    // Firestore helpers
    const updateBillingField = async (updates, successMsg) => {
        if (!firestore || !orgId) return;
        try {
            const orgRef = doc(firestore, 'organizations', orgId);
            await updateDoc(orgRef, { billing: { ...billing, ...updates } });
            if (onOrganizationUpdate) {
                onOrganizationUpdate({ billing: { ...billing, ...updates } });
            }
            toast.success(successMsg || 'Updated successfully');
        } catch (err) {
            console.error('Error updating billing:', err);
            toast.error('Failed to update: ' + err.message);
        }
    };

    // --- Status Tab Actions ---

    const handleToggleExempt = async () => {
        const newValue = !billingExempt;
        const msg = newValue
            ? `This will exempt "${organizationData.name}" from ALL billing. No invoices will be generated, no lifecycle actions will run, and no banners will display. Continue?`
            : `This will re-enable billing for "${organizationData.name}". They will be included in the next billing cycle. Continue?`;
        if (!window.confirm(msg)) return;

        setActionInProgress('exempt');
        await updateBillingField(
            newValue
                ? { billingExempt: true }
                : { billingExempt: false, billingExemptReason: null, billingExemptNote: null },
            newValue ? 'Organization is now billing-exempt' : 'Billing exemption removed',
        );
        setActionInProgress(null);
    };

    const STATUS_LABELS = { current: 'Current', grace_period: 'Grace Period', restricted: 'Restricted', suspended: 'Suspended' };

    const applyStatusChange = async (newStatus) => {
        setActionInProgress('status-' + newStatus);
        const adminUid = getAuth()?.currentUser?.uid || 'admin-dashboard';
        const updates = {
            billingStatus: newStatus,
            billingStatusUpdatedAt: Date.now(),
            lastManualOverrideAt: Date.now(),
            lastManualOverrideBy: adminUid,
            lastReminderDay: 0,
        };

        if (newStatus === 'current') {
            updates.graceDeadline = null;
            updates.restrictedDeadline = null;
        } else if (newStatus === 'grace_period') {
            updates.graceDeadline = Date.now() + 7 * MS_PER_DAY;
            updates.restrictedDeadline = null;
        } else if (newStatus === 'restricted') {
            updates.restrictedDeadline = Date.now() + 14 * MS_PER_DAY;
        }

        await updateBillingField(updates, `Status changed to ${STATUS_LABELS[newStatus]}`);
        setActionInProgress(null);
    };

    const handleStatusOverride = async (newStatus) => {
        const msg = `Override billing status to "${STATUS_LABELS[newStatus]}" for "${organizationData.name}"? This is a manual override.`;
        if (!window.confirm(msg)) return;
        await applyStatusChange(newStatus);
    };

    const handleExtendDeadline = async (deadlineField, days) => {
        const currentDeadline = billing[deadlineField] || Date.now();
        const newDeadline = Math.max(currentDeadline, Date.now()) + days * MS_PER_DAY;
        setActionInProgress('extend');
        await updateBillingField(
            { [deadlineField]: newDeadline },
            `Deadline extended by ${days} days`,
        );
        setActionInProgress(null);
    };

    // --- Configuration Tab Actions ---

    const handleSaveConfig = async () => {
        setSavingConfig(true);

        if (configForm.billingType === 'per_user') {
            const perUserConfig = await createPerUserPrices();
            if (perUserConfig && onOrganizationUpdate) {
                onOrganizationUpdate({
                    billing: { ...billing, billingType: 'per_user', billingMode: configForm.billingMode, perUserConfig },
                });
            }
        } else {
            // Switching from per_user → per_loan: cancel the active Stripe subscription first
            if (
                billing.billingType === 'per_user' &&
                organizationData?.subscription?.status === 'active' &&
                organizationData?.subscription?.provider === 'stripe'
            ) {
                if (!window.confirm('This will cancel the active per-user Stripe subscription. Continue?')) {
                    setSavingConfig(false);
                    return;
                }
                try {
                    const cancelResp = await fetch(activeMainApiUrl + '/subscription/cancel-enterprise-subscription', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ organizationId: orgId }),
                    });
                    const cancelData = await cancelResp.json();
                    if (!cancelData.success) {
                        toast.error('Failed to cancel subscription: ' + (cancelData.reason || 'Unknown error'));
                        setSavingConfig(false);
                        return;
                    }
                    toast.info('Active per-user subscription has been canceled');
                } catch (cancelErr) {
                    toast.error('Error canceling subscription: ' + cancelErr.message);
                    setSavingConfig(false);
                    return;
                }
            }

            // Per-loan: existing Firestore-direct save path
            const pricingForm = { ...perLoanForm, billingMode: configForm.billingMode };
            const pricingError = validatePerLoanForm(pricingForm);
            if (pricingError) {
                toast.error(pricingError);
                setSavingConfig(false);
                return;
            }

            const configUpdates = buildPerLoanBilling(pricingForm);

            if (!billing.billingStatus) {
                configUpdates.billingStatus = 'current';
                configUpdates.billingStatusUpdatedAt = Date.now();
            }
            if (billing.billingExempt === undefined) {
                configUpdates.billingExempt = false;
            }

            await updateBillingField(configUpdates, 'Billing configuration saved');
        }

        setSavingConfig(false);
    };

    /** Creates the org's per-user Stripe prices (the backend saves the config); returns perUserConfig or null. */
    const createPerUserPrices = async () => {
        if (configForm.standardSeatPrice <= 0) {
            toast.error('Standard seat price must be greater than zero');

            return null;
        }
        try {
            const resp = await fetch(activeMainApiUrl + '/subscription/create-enterprise-per-user-prices', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    organizationId: orgId,
                    standardSeatPrice: Number(configForm.standardSeatPrice),
                    assistantSeatPrice: Number(configForm.assistantSeatPrice) || 0,
                    cycle: configForm.cycle,
                    billingMode: configForm.billingMode,
                }),
            });
            const data = await resp.json();
            if (!data.success) {
                toast.error('Failed to save: ' + (data.reason || 'Unknown error'));

                return null;
            }
            toast.success('Per-user billing configuration saved and Stripe prices created');

            return {
                standardSeatPrice: Number(configForm.standardSeatPrice),
                assistantSeatPrice: Number(configForm.assistantSeatPrice) || 0,
                cycle: configForm.cycle,
                stripePriceIdStandard: data.stripePriceIdStandard,
                stripePriceIdAssistant: data.stripePriceIdAssistant,
            };
        } catch (err) {
            toast.error('Failed to save per-user config: ' + err.message);

            return null;
        }
    };

    // --- Set to Enterprise (activation) ---

    const buildActivationBilling = async () => {
        const adminUid = getAuth()?.currentUser?.uid || 'admin-dashboard';
        const notExempt = { billingExempt: false, billingExemptReason: null, billingExemptNote: null };
        const status = {
            billingStatus: billing.billingStatus || 'current',
            billingStatusUpdatedAt: billing.billingStatusUpdatedAt || Date.now(),
        };

        if (activationChoice === 'exempt') {
            if (!exemptReason) {
                toast.error('Pick a reason for the exemption');

                return null;
            }
            if (exemptReason === 'other' && !exemptNote.trim()) {
                toast.error('Describe the reason for the exemption');

                return null;
            }

            return {
                billingExempt: true,
                billingExemptReason: exemptReason,
                billingExemptNote: exemptReason === 'other' ? exemptNote.trim() : null,
                billingExemptSetAt: Date.now(),
                billingExemptSetBy: adminUid,
            };
        }

        if (activationChoice === 'growth') {
            return {
                billingType: 'per_user',
                enterpriseGrowth: true,
                enterpriseGrowthSetAt: Date.now(),
                enterpriseGrowthSetBy: adminUid,
                billingMode: billing.billingMode || 'auto_charge',
                ...status,
                ...notExempt,
            };
        }

        if (configForm.billingType === 'per_user') {
            const perUserConfig = await createPerUserPrices();
            if (!perUserConfig) return null;

            return { billingType: 'per_user', billingMode: configForm.billingMode, perUserConfig, ...status, ...notExempt };
        }

        const pricingForm = { ...perLoanForm, billingMode: configForm.billingMode };
        if (isNewOrganization && !pricingForm.billingStartDate) {
            toast.error('Pick the date billing starts');

            return null;
        }
        const pricingError = validatePerLoanForm(pricingForm);
        if (pricingError) {
            toast.error(pricingError);

            return null;
        }

        return { ...buildPerLoanBilling(pricingForm), ...status, ...notExempt };
    };

    const handleActivate = async () => {
        setSavingConfig(true);
        const billingUpdates = await buildActivationBilling();
        setSavingConfig(false);
        if (billingUpdates) onActivate(billingUpdates);
    };

    // Generate checkout link for per-user subscription
    const handleGenerateCheckoutLink = async () => {
        setGeneratingCheckout(true);
        try {
            const resp = await fetch(activeMainApiUrl + '/subscription/create-enterprise-checkout-session', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ organizationId: orgId }),
            });
            const data = await resp.json();
            if (data.success) {
                setCheckoutUrl(data.checkoutUrl);
                toast.success(`Checkout link generated (${data.seatBreakdown?.standard || 0} std, ${data.seatBreakdown?.assistant || 0} ast seats)`);
            } else {
                toast.error('Failed: ' + (data.reason || 'Unknown error'));
            }
        } catch (err) {
            toast.error('Failed to generate checkout link: ' + err.message);
        }
        setGeneratingCheckout(false);
    };

    const handleCopyCheckoutLink = () => {
        navigator.clipboard.writeText(checkoutUrl);
        toast.success('Checkout link copied to clipboard');
    };

    // --- Backfill Actions ---

    const handleEnableBackfill = async () => {
        if (!backfillMonth) {
            toast.error('Please select a start month for backfill');
            return;
        }
        const now = new Date();
        let prevYear = now.getUTCFullYear();
        let prevMonth = now.getUTCMonth();
        if (prevMonth === 0) { prevMonth = 12; prevYear -= 1; }
        const prevMonthKey = `${prevYear}-${String(prevMonth).padStart(2, '0')}`;

        if (backfillMonth > prevMonthKey) {
            toast.error('Backfill start month cannot be after the previous calendar month');
            return;
        }

        const msg = `Enable backfill from ${backfillMonth} through ${prevMonthKey}? The next billing cron run will generate invoices for all months in this range. Existing invoices will not be duplicated.`;
        if (!window.confirm(msg)) return;

        setSavingBackfill(true);
        await updateBillingField({
            backfillFromMonth: backfillMonth,
            backfillSendEmails: backfillSendEmails,
        }, `Backfill enabled from ${backfillMonth}`);
        setSavingBackfill(false);
    };

    const handleCancelBackfill = async () => {
        if (!window.confirm('Cancel the pending backfill? No new backfill invoices will be generated on the next cron run.')) return;
        setSavingBackfill(true);
        await updateBillingField({ backfillFromMonth: null, backfillSendEmails: null }, 'Backfill cancelled');
        setBackfillMonth('');
        setSavingBackfill(false);
    };

    // --- Invoice Tab Actions ---

    const handleForgiveInvoice = async (invoice) => {
        if (!window.confirm(`Forgive invoice for ${invoice.monthKey} ($${invoice.totalAmount?.toFixed(2)})? This will void the Stripe invoice and mark it as forgiven.`)) return;

        setActionInProgress('forgive-' + invoice.id);
        try {
            const adminUserId = getAuth()?.currentUser?.uid;
            const resp = await fetch(activeApiUrl + '/admin/billing/void-invoice', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId: adminUserId,
                    organizationId: orgId,
                    invoiceId: invoice.id,
                }),
            });
            const data = await resp.json();
            if (data.success) {
                toast.success(`Invoice forgiven${data.stripeVoided ? ' and voided in Stripe' : ''}`);
                fetchInvoices();
            } else {
                toast.error('Failed: ' + (data.reason || 'Unknown error'));
            }
        } catch (err) {
            toast.error('Failed to forgive invoice: ' + err.message);
        }
        setActionInProgress(null);
    };

    const handleMarkPaid = async (invoice) => {
        if (!window.confirm(`Mark invoice for ${invoice.monthKey} ($${invoice.totalAmount?.toFixed(2)}) as paid? Use this for out-of-band payments (wire, check, etc.)`)) return;

        setActionInProgress('markpaid-' + invoice.id);
        try {
            const adminUserId = getAuth()?.currentUser?.uid;
            const resp = await fetch(activeApiUrl + '/admin/billing/mark-paid', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId: adminUserId,
                    organizationId: orgId,
                    invoiceId: invoice.id,
                }),
            });
            const data = await resp.json();
            if (data.success) {
                toast.success('Invoice marked as paid');
                if (data.allInvoicesResolved && billingStatus !== 'current') {
                    if (window.confirm('All invoices are now resolved. Reset billing status to "Current"?')) {
                        await applyStatusChange('current');
                    }
                }
                fetchInvoices();
            } else {
                toast.error('Failed: ' + (data.reason || 'Unknown error'));
            }
        } catch (err) {
            toast.error('Failed to mark paid: ' + err.message);
        }
        setActionInProgress(null);
    };

    const unpaidInvoices = invoices.filter(inv => ['open', 'payment_failed', 'uncollectible'].includes(inv.status));
    const unpaidTotal = unpaidInvoices.reduce((sum, inv) => sum + (inv.totalAmount || 0), 0);

    if (!isOpen) return null;

    return (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
            <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
                <div className="modal-content">
                    {/* Header with Exempt Toggle */}
                    <div className="modal-header">
                        <div className="d-flex flex-column">
                            <h5 className="modal-title mb-0">
                                <FaDollarSign className="me-2" />
                                {isActivation ? 'Set to Enterprise' : 'Enterprise Billing'} — {organizationData?.name || 'Organization'}
                            </h5>
                            <small className="text-muted">
                                {isActivation ? 'Confirm how this organization is billed.' : `Org ID: ${orgId}`}
                            </small>
                        </div>
                        <div className="d-flex align-items-center gap-3">
                            {/* Billing Exempt Toggle */}
                            {!isActivation && (
                            <div className="d-flex align-items-center gap-2">
                                {billingExempt && (
                                    <span className="badge bg-success" title={getExemptReasonLabel(billing) || undefined}>
                                        EXEMPT{getExemptReasonLabel(billing) ? ` · ${getExemptReasonLabel(billing)}` : ''}
                                    </span>
                                )}
                                <label className="form-check-label small fw-bold" htmlFor="billingExemptToggle">
                                    Billing Exempt
                                </label>
                                <div className="form-check form-switch mb-0">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        role="switch"
                                        id="billingExemptToggle"
                                        checked={billingExempt}
                                        onChange={handleToggleExempt}
                                        disabled={actionInProgress === 'exempt'}
                                        style={{ cursor: 'pointer' }}
                                    />
                                </div>
                            </div>
                            )}
                            <button
                                type="button"
                                className="btn-close"
                                onClick={onClose}
                            ></button>
                        </div>
                    </div>

                    {/* Tab Navigation */}
                    {!isActivation && (
                    <div className="px-3 pt-2" style={{ borderBottom: '1px solid #dee2e6' }}>
                        <ul className="nav nav-tabs border-bottom-0">
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'status' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('status')}
                                >
                                    <FaHeartbeat className="me-1" /> Status & Health
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'config' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('config')}
                                >
                                    <FaCog className="me-1" /> Configuration
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'invoices' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('invoices')}
                                >
                                    <FaFileInvoiceDollar className="me-1" /> Invoices
                                    {unpaidInvoices.length > 0 && activeTab !== 'invoices' && (
                                        <span className="badge bg-danger ms-1">{unpaidInvoices.length}</span>
                                    )}
                                </button>
                            </li>
                        </ul>
                    </div>
                    )}

                    {/* Tab Content */}
                    <div className="modal-body" style={{ minHeight: '400px' }}>
                        {/* ===== STATUS & HEALTH TAB ===== */}
                        {activeTab === 'status' && (
                            <div>
                                {/* Status Card */}
                                <div className="card mb-4">
                                    <div className="card-body">
                                        <div className="d-flex justify-content-between align-items-start">
                                            <div>
                                                <h6 className="text-muted mb-2">Current Billing Status</h6>
                                                <span
                                                    className={`badge ${STATUS_STYLES[billingStatus]?.badge || 'bg-secondary'} fs-6 px-3 py-2`}
                                                    style={STATUS_STYLES[billingStatus]?.customStyle || {}}
                                                >
                                                    {STATUS_STYLES[billingStatus]?.label || billingStatus}
                                                </span>
                                                {statusUpdatedAt > 0 && (
                                                    <div className="mt-2 text-muted small">
                                                        In this state for <strong>{daysInState} day{daysInState !== 1 ? 's' : ''}</strong>
                                                        {' '}(since {new Date(statusUpdatedAt).toLocaleDateString()})
                                                    </div>
                                                )}
                                                {billing.lastReminderDay > 0 && (
                                                    <div className="text-muted small">
                                                        Last automated reminder: Day {billing.lastReminderDay}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="text-end">
                                                {billingExempt && (
                                                    <div className="alert alert-success py-2 px-3 mb-0">
                                                        <small><strong>This org is billing-exempt.</strong><br />No billing actions will be taken.</small>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Deadlines */}
                                        {billingStatus === 'grace_period' && graceDeadline && (
                                            <div className="mt-3 p-3 bg-light rounded">
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <div>
                                                        <strong>Grace Deadline:</strong> {new Date(graceDeadline).toLocaleDateString()}
                                                        {graceRemaining != null && (
                                                            <span className={`ms-2 badge ${graceRemaining <= 2 ? 'bg-danger' : 'bg-warning text-dark'}`}>
                                                                {graceRemaining} day{graceRemaining !== 1 ? 's' : ''} remaining
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="d-flex gap-1">
                                                        <button className="btn btn-sm btn-outline-secondary" onClick={() => handleExtendDeadline('graceDeadline', 3)} disabled={!!actionInProgress}>+3 days</button>
                                                        <button className="btn btn-sm btn-outline-secondary" onClick={() => handleExtendDeadline('graceDeadline', 7)} disabled={!!actionInProgress}>+7 days</button>
                                                        <button className="btn btn-sm btn-outline-secondary" onClick={() => handleExtendDeadline('graceDeadline', 14)} disabled={!!actionInProgress}>+14 days</button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {billingStatus === 'restricted' && restrictedDeadline && (
                                            <div className="mt-3 p-3 bg-light rounded">
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <div>
                                                        <strong>Restricted Deadline:</strong> {new Date(restrictedDeadline).toLocaleDateString()}
                                                        {restrictedRemaining != null && (
                                                            <span className={`ms-2 badge ${restrictedRemaining <= 3 ? 'bg-danger' : 'bg-warning text-dark'}`}>
                                                                {restrictedRemaining} day{restrictedRemaining !== 1 ? 's' : ''} remaining
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="d-flex gap-1">
                                                        <button className="btn btn-sm btn-outline-secondary" onClick={() => handleExtendDeadline('restrictedDeadline', 3)} disabled={!!actionInProgress}>+3 days</button>
                                                        <button className="btn btn-sm btn-outline-secondary" onClick={() => handleExtendDeadline('restrictedDeadline', 7)} disabled={!!actionInProgress}>+7 days</button>
                                                        <button className="btn btn-sm btn-outline-secondary" onClick={() => handleExtendDeadline('restrictedDeadline', 14)} disabled={!!actionInProgress}>+14 days</button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Outstanding Balance */}
                                <div className="card mb-4">
                                    <div className="card-body">
                                        <h6 className="text-muted mb-2">Outstanding Balance</h6>
                                        {unpaidInvoices.length > 0 ? (
                                            <div>
                                                <span className="badge bg-danger fs-6 px-3 py-2">
                                                    {unpaidInvoices.length} unpaid invoice{unpaidInvoices.length !== 1 ? 's' : ''} — ${unpaidTotal.toFixed(2)}
                                                </span>
                                                <div className="mt-2">
                                                    <button className="btn btn-sm btn-outline-primary" onClick={() => setActiveTab('invoices')}>
                                                        View Invoices
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <span className="text-success">No outstanding invoices</span>
                                        )}
                                    </div>
                                </div>

                                {/* Status Override Actions */}
                                <div className="card border-danger">
                                    <div className="card-header bg-danger bg-opacity-10">
                                        <h6 className="mb-0 text-danger">Manual Status Override</h6>
                                    </div>
                                    <div className="card-body">
                                        <p className="text-muted small mb-3">
                                            These actions manually override the billing lifecycle. Use with caution.
                                        </p>
                                        <div className="d-flex gap-2 flex-wrap">
                                            {billingStatus !== 'current' && (
                                                <button
                                                    className="btn btn-sm btn-success"
                                                    onClick={() => handleStatusOverride('current')}
                                                    disabled={!!actionInProgress}
                                                >
                                                    {actionInProgress === 'status-current' ? 'Resetting...' : 'Reset to Current'}
                                                </button>
                                            )}
                                            {billingStatus !== 'grace_period' && (
                                                <button
                                                    className="btn btn-sm btn-warning"
                                                    onClick={() => handleStatusOverride('grace_period')}
                                                    disabled={!!actionInProgress}
                                                >
                                                    {actionInProgress === 'status-grace_period' ? 'Setting...' : 'Force Grace Period'}
                                                </button>
                                            )}
                                            {billingStatus !== 'restricted' && (
                                                <button
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={() => handleStatusOverride('restricted')}
                                                    disabled={!!actionInProgress}
                                                >
                                                    {actionInProgress === 'status-restricted' ? 'Setting...' : 'Force Restricted'}
                                                </button>
                                            )}
                                            {billingStatus !== 'suspended' && (
                                                <button
                                                    className="btn btn-sm btn-danger"
                                                    onClick={() => handleStatusOverride('suspended')}
                                                    disabled={!!actionInProgress}
                                                >
                                                    {actionInProgress === 'status-suspended' ? 'Setting...' : 'Force Suspended'}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ===== CONFIGURATION TAB ===== */}
                        {activeTab === 'config' && (
                            <div>
                                {isActivation && (
                                    <div className="card mb-4">
                                        <div className="card-body">
                                            <div className="form-check mb-2">
                                                <input
                                                    className="form-check-input"
                                                    type="radio"
                                                    id="activationChoiceBill"
                                                    checked={activationChoice === 'bill'}
                                                    onChange={() => setActivationChoice('bill')}
                                                />
                                                <label className="form-check-label fw-bold" htmlFor="activationChoiceBill">
                                                    Bill this organization
                                                </label>
                                                <div className="text-muted small">Set up billing below: $99 per funded loan from today by default.</div>
                                            </div>
                                            {canKeepGrowthBilling && (
                                                <div className="form-check mb-2">
                                                    <input
                                                        className="form-check-input"
                                                        type="radio"
                                                        id="activationChoiceGrowth"
                                                        checked={activationChoice === 'growth'}
                                                        onChange={() => setActivationChoice('growth')}
                                                    />
                                                    <label className="form-check-label fw-bold" htmlFor="activationChoiceGrowth">
                                                        Enterprise Growth: keep Growth billing
                                                    </label>
                                                    <div className="text-muted small">
                                                        Full Enterprise access. They keep paying their Growth subscription in Stripe; no per-loan invoices.
                                                    </div>
                                                </div>
                                            )}
                                            <div className="form-check">
                                                <input
                                                    className="form-check-input"
                                                    type="radio"
                                                    id="activationChoiceExempt"
                                                    checked={activationChoice === 'exempt'}
                                                    onChange={() => setActivationChoice('exempt')}
                                                />
                                                <label className="form-check-label fw-bold" htmlFor="activationChoiceExempt">
                                                    Billing exempt
                                                </label>
                                                <div className="text-muted small">No invoices, no lifecycle actions, no banners.</div>
                                            </div>
                                            {activationChoice === 'exempt' && (
                                                <div className="ms-4 mt-2" style={{ maxWidth: '360px' }}>
                                                    <label className="form-label fw-bold small mb-1">Reason</label>
                                                    <select
                                                        className="form-select form-select-sm"
                                                        value={exemptReason}
                                                        onChange={(e) => setExemptReason(e.target.value)}
                                                    >
                                                        <option value="">Select a reason…</option>
                                                        {EXEMPT_REASONS.map((r) => (
                                                            <option key={r.value} value={r.value}>{r.label}</option>
                                                        ))}
                                                    </select>
                                                    {exemptReason === 'other' && (
                                                        <input
                                                            type="text"
                                                            className="form-control form-control-sm mt-2"
                                                            placeholder="Why is this org exempt?"
                                                            value={exemptNote}
                                                            onChange={(e) => setExemptNote(e.target.value)}
                                                        />
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {(!isActivation || activationChoice === 'bill') && (
                                <>
                                {!isActivation && !hasBillingConfig && !billing.perUserConfig && (
                                    <div className="alert alert-info mb-4">
                                        <strong>No billing configuration found.</strong> Set up the pricing below to enable billing for this organization.
                                    </div>
                                )}

                                <div className="card">
                                    <div className="card-body">
                                        <h6 className="mb-3">Billing Configuration</h6>
                                        <div className="row g-3">
                                            {/* Billing Type */}
                                            <div className="col-md-6">
                                                <label className="form-label fw-bold">Billing Type</label>
                                                <select
                                                    className="form-select"
                                                    value={configForm.billingType}
                                                    onChange={(e) => {
                                                        const newType = e.target.value;
                                                        if (billing.billingType && billing.billingType !== newType) {
                                                            if (!window.confirm(`Switch billing type from "${billing.billingType}" to "${newType}"? This will change how this organization is billed.`)) return;
                                                        }
                                                        setConfigForm(prev => ({ ...prev, billingType: newType }));
                                                    }}
                                                >
                                                    <option value="per_loan">Per Loan (usage-based invoicing)</option>
                                                    <option value="per_user" disabled={isNewOrganization}>
                                                        Per User (seat-based subscription){isNewOrganization ? ' — once the organization exists' : ''}
                                                    </option>
                                                </select>
                                                <small className="text-muted">
                                                    {configForm.billingType === 'per_loan'
                                                        ? 'Monthly invoices based on loan volume processed.'
                                                        : 'Recurring Stripe subscription based on team seat count.'}
                                                </small>
                                            </div>

                                            {/* Billing Mode — shared by both types */}
                                            <BillingModeSelect
                                                value={configForm.billingMode}
                                                onChange={(billingMode) => setConfigForm(prev => ({ ...prev, billingMode }))}
                                            />

                                            {/* ---- Per-Loan Fields ---- */}
                                            {configForm.billingType === 'per_loan' && (
                                                <>
                                                    <PerLoanPricingFields form={perLoanForm} onChange={setPerLoanForm} />

                                                    {/* ---- Billing Stages (per LOS) ---- */}
                                                    {isNewOrganization ? (
                                                        <div className="col-12">
                                                            <hr className="my-3" />
                                                            <small className="text-muted">
                                                                Billing stages use the built-in rules. Customize them in Enterprise Billing once the organization exists and its LOS is connected.
                                                            </small>
                                                        </div>
                                                    ) : (
                                                    <div className="col-12">
                                                        <hr className="my-3" />
                                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                                            <div>
                                                                <label className="form-label fw-bold mb-0">Billing Stages</label>
                                                                <div className="text-muted small">
                                                                    Which LOS stages count a loan as funded (billable) or dead for this organization.
                                                                </div>
                                                            </div>
                                                            {hasCustomRulesForSelected ? (
                                                                <span className="badge bg-info">Custom rules active</span>
                                                            ) : (
                                                                <span className="badge bg-secondary">Using built-in defaults</span>
                                                            )}
                                                        </div>

                                                        <div className="row g-3 mb-3">
                                                            <div className="col-md-6">
                                                                <label className="form-label fw-bold">Loan Origination System</label>
                                                                <select
                                                                    className="form-select"
                                                                    value={selectedLos}
                                                                    onChange={(e) => setSelectedLos(e.target.value)}
                                                                >
                                                                    {losOptions.map((entry) => (
                                                                        <option key={entry.losKey} value={entry.losKey}>
                                                                            {entry.label}
                                                                            {entry.connected ? ' — connected' : ''}
                                                                            {savedStageRules[entry.losKey] ? ' (customized)' : ''}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                                <small className="text-muted">
                                                                    Rules are saved per LOS. Loans are matched by their own LOS, so an org
                                                                    using several keeps correct rules for each.
                                                                </small>
                                                            </div>
                                                        </div>

                                                        {loadingStageDefaults && (
                                                            <div className="text-muted small mb-2">Loading stage defaults…</div>
                                                        )}

                                                        {['funded', 'dead'].map((bucket) => (
                                                            <div key={bucket} className="mb-3">
                                                                <div className="fw-bold mb-1">
                                                                    {bucket === 'funded' ? 'Funded / billable stages' : 'Dead stages (never billed)'}
                                                                </div>
                                                                <table className="table table-sm align-middle mb-1">
                                                                    <thead>
                                                                        <tr>
                                                                            <th style={{ width: '190px' }}>Match</th>
                                                                            <th>Value</th>
                                                                            <th style={{ width: '60px' }}></th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {stageRules[bucket].length === 0 && (
                                                                            <tr>
                                                                                <td colSpan={3} className="text-muted small">
                                                                                    No rules — {bucket === 'funded'
                                                                                        ? 'nothing will bill for this LOS.'
                                                                                        : 'no stage is treated as dead.'}
                                                                                </td>
                                                                            </tr>
                                                                        )}
                                                                        {stageRules[bucket].map((rule, index) => (
                                                                            <tr key={`${bucket}-${index}`}>
                                                                                <td>
                                                                                    <select
                                                                                        className="form-select form-select-sm"
                                                                                        value={rule.match}
                                                                                        onChange={(e) =>
                                                                                            updateStageRule(bucket, index, { match: e.target.value })
                                                                                        }
                                                                                    >
                                                                                        {stageMatchTypes.map((match) => (
                                                                                            <option key={match} value={match}>
                                                                                                {match}
                                                                                            </option>
                                                                                        ))}
                                                                                    </select>
                                                                                </td>
                                                                                <td>
                                                                                    <input
                                                                                        type={rule.match === 'stageId' ? 'number' : 'text'}
                                                                                        className="form-control form-control-sm"
                                                                                        value={rule.value ?? ''}
                                                                                        placeholder={STAGE_MATCH_HELP[rule.match] || ''}
                                                                                        onChange={(e) =>
                                                                                            updateStageRule(bucket, index, { value: e.target.value })
                                                                                        }
                                                                                    />
                                                                                    {rule.label && (
                                                                                        <small className="text-muted">{rule.label}</small>
                                                                                    )}
                                                                                </td>
                                                                                <td>
                                                                                    <button
                                                                                        type="button"
                                                                                        className="btn btn-sm btn-outline-danger"
                                                                                        onClick={() => removeStageRule(bucket, index)}
                                                                                        title="Remove rule"
                                                                                    >
                                                                                        ×
                                                                                    </button>
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-outline-secondary"
                                                                    onClick={() => addStageRule(bucket)}
                                                                >
                                                                    + Add {bucket} stage
                                                                </button>
                                                            </div>
                                                        ))}

                                                        <div className="d-flex gap-2 align-items-center">
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-primary"
                                                                disabled={savingStageRules}
                                                                onClick={handleSaveStageRules}
                                                            >
                                                                {savingStageRules ? 'Saving…' : 'Save Stage Rules'}
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-secondary"
                                                                disabled={savingStageRules || !stageDefaults}
                                                                onClick={resetStageRulesToDefaults}
                                                            >
                                                                Reset to defaults
                                                            </button>
                                                            {hasCustomRulesForSelected && (
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-outline-danger"
                                                                    disabled={savingStageRules}
                                                                    onClick={handleClearStageRules}
                                                                    title="Delete this org's custom rules so the built-in ladder applies again"
                                                                >
                                                                    Remove customization
                                                                </button>
                                                            )}
                                                        </div>
                                                        <small className="text-muted d-block mt-2">
                                                            Saving replaces the built-in ladder for <strong>{selectedLos || 'this LOS'}</strong> entirely.
                                                            Other LOS keep their defaults.
                                                        </small>
                                                    </div>
                                                    )}
                                                </>
                                            )}

                                            {/* ---- Per-User Fields ---- */}
                                            {configForm.billingType === 'per_user' && (
                                                <>
                                                    <div className="col-md-4">
                                                        <label className="form-label fw-bold">Standard Seat Price ($)</label>
                                                        <div className="input-group">
                                                            <span className="input-group-text">$</span>
                                                            <input
                                                                type="number"
                                                                className="form-control"
                                                                min="0"
                                                                step="1"
                                                                value={configForm.standardSeatPrice}
                                                                onChange={(e) => setConfigForm(prev => ({ ...prev, standardSeatPrice: parseFloat(e.target.value) || 0 }))}
                                                            />
                                                        </div>
                                                        <small className="text-muted">Monthly or yearly price per standard seat.</small>
                                                    </div>

                                                    <div className="col-md-4">
                                                        <label className="form-label fw-bold">Assistant Seat Price ($)</label>
                                                        <div className="input-group">
                                                            <span className="input-group-text">$</span>
                                                            <input
                                                                type="number"
                                                                className="form-control"
                                                                min="0"
                                                                step="1"
                                                                value={configForm.assistantSeatPrice}
                                                                onChange={(e) => setConfigForm(prev => ({ ...prev, assistantSeatPrice: parseFloat(e.target.value) || 0 }))}
                                                            />
                                                        </div>
                                                        <small className="text-muted">Optional. Leave 0 if not offering assistant seats.</small>
                                                    </div>

                                                    <div className="col-md-4">
                                                        <label className="form-label fw-bold">Billing Cycle</label>
                                                        <select
                                                            className="form-select"
                                                            value={configForm.cycle}
                                                            onChange={(e) => setConfigForm(prev => ({ ...prev, cycle: e.target.value }))}
                                                        >
                                                            <option value="month">Monthly</option>
                                                            <option value="year">Yearly</option>
                                                        </select>
                                                    </div>
                                                </>
                                            )}
                                        </div>

                                        {/* Config Summary */}
                                        <div className="mt-4 p-3 bg-light rounded">
                                            <h6>Summary</h6>
                                            {configForm.billingType === 'per_loan' ? (
                                                <div className="row">
                                                    <div className="col-md-4">
                                                        <small className="text-muted d-block">Mode</small>
                                                        <strong>{configForm.billingMode === 'auto_charge' ? 'Auto Charge' : 'Invoice (Net-30)'}</strong>
                                                    </div>
                                                    <div className="col-md-4">
                                                        <small className="text-muted d-block">Price Per Loan</small>
                                                        <strong>${Number(perLoanForm.pricePerLoan).toFixed(2)}</strong>
                                                    </div>
                                                    <div className="col-md-4">
                                                        <small className="text-muted d-block">Discount</small>
                                                        <strong>{perLoanForm.discountRate}%</strong>
                                                    </div>
                                                    {perLoanForm.hasIntroPrice && (
                                                        <div className="col-12 mt-2">
                                                            <small className="text-muted d-block">Intro Price</small>
                                                            <strong>
                                                                ${Number(perLoanForm.introPricePerLoan).toFixed(2)} per loan for the first {perLoanForm.introDays} days
                                                            </strong>
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="row">
                                                    <div className="col-md-3">
                                                        <small className="text-muted d-block">Standard Seat</small>
                                                        <strong>${configForm.standardSeatPrice}/{configForm.cycle}</strong>
                                                    </div>
                                                    <div className="col-md-3">
                                                        <small className="text-muted d-block">Assistant Seat</small>
                                                        <strong>{configForm.assistantSeatPrice > 0 ? `$${configForm.assistantSeatPrice}/${configForm.cycle}` : 'N/A'}</strong>
                                                    </div>
                                                    <div className="col-md-3">
                                                        <small className="text-muted d-block">Mode</small>
                                                        <strong>{configForm.billingMode === 'auto_charge' ? 'Auto Charge' : 'Invoice (Net-30)'}</strong>
                                                    </div>
                                                    <div className="col-md-3">
                                                        <small className="text-muted d-block">Subscription</small>
                                                        <strong>
                                                            {organizationData?.subscription?.status === 'active'
                                                                ? <span className="badge bg-success">Active</span>
                                                                : <span className="badge bg-secondary">Not Subscribed</span>}
                                                        </strong>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {!isActivation && (
                                        <div className="mt-3 d-flex gap-2">
                                            <button
                                                className="btn btn-primary"
                                                onClick={handleSaveConfig}
                                                disabled={savingConfig}
                                            >
                                                {savingConfig ? 'Saving...' : (hasBillingConfig || billing.perUserConfig ? 'Save Configuration' : 'Save & Enable Billing')}
                                            </button>
                                        </div>
                                        )}
                                    </div>
                                </div>

                                {/* Per-User: Checkout Link Generation */}
                                {configForm.billingType === 'per_user' && billing.perUserConfig?.stripePriceIdStandard && (
                                    <div className="card mt-4 border-primary">
                                        <div className="card-header bg-primary bg-opacity-10">
                                            <h6 className="mb-0"><FaUsers className="me-1" /> Subscription Checkout</h6>
                                        </div>
                                        <div className="card-body">
                                            {organizationData?.subscription?.status === 'active' && organizationData?.subscription?.provider === 'stripe' ? (
                                                <div className="alert alert-success mb-0">
                                                    <strong>Active subscription.</strong> This organization has an active Stripe subscription. Seat changes are managed automatically as members are added or removed.
                                                    {organizationData?.subscription?.id && (
                                                        <div className="mt-1">
                                                            <small className="text-muted">Subscription ID: {organizationData.subscription.id}</small>
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <div>
                                                    <p className="text-muted small mb-3">
                                                        Generate a Stripe Checkout link for this organization. The link will include the correct seat counts and pricing. Share this link with the customer to complete their subscription.
                                                    </p>
                                                    <div className="d-flex gap-2 align-items-center">
                                                        <button
                                                            className="btn btn-primary"
                                                            onClick={handleGenerateCheckoutLink}
                                                            disabled={generatingCheckout}
                                                        >
                                                            {generatingCheckout ? 'Generating...' : 'Generate Checkout Link'}
                                                        </button>
                                                        {checkoutUrl && (
                                                            <>
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    value={checkoutUrl}
                                                                    readOnly
                                                                    style={{ maxWidth: '400px' }}
                                                                />
                                                                <button
                                                                    className="btn btn-outline-secondary"
                                                                    onClick={handleCopyCheckoutLink}
                                                                    title="Copy to clipboard"
                                                                >
                                                                    <FaCopy />
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                    {checkoutUrl && (
                                                        <small className="text-muted mt-2 d-block">
                                                            This link expires after 24 hours. Generate a new one if needed.
                                                        </small>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Backfill Section — only for per-loan billing */}
                                {configForm.billingType === 'per_loan' && hasBillingConfig && (
                                    <div className="card mt-4 border-warning">
                                        <div className="card-header bg-warning bg-opacity-10">
                                            <h6 className="mb-0">Invoice Backfill</h6>
                                        </div>
                                        <div className="card-body">
                                            {billing.backfillFromMonth ? (
                                                <div>
                                                    <div className="alert alert-warning mb-3">
                                                        <strong>Backfill pending.</strong> The next billing cron run will generate invoices for all months from <strong>{billing.backfillFromMonth}</strong> onward. Existing invoices will not be duplicated.
                                                        <br />
                                                        <small>Customer email notifications: <strong>{billing.backfillSendEmails ? 'On' : 'Off'}</strong></small>
                                                    </div>
                                                    <button
                                                        className="btn btn-outline-danger btn-sm"
                                                        onClick={handleCancelBackfill}
                                                        disabled={savingBackfill}
                                                    >
                                                        {savingBackfill ? 'Cancelling...' : 'Cancel Backfill'}
                                                    </button>
                                                </div>
                                            ) : (
                                                <div>
                                                    <p className="text-muted small mb-3">
                                                        Generate invoices for previous months that were not billed. The billing cron will create invoices for each month from the selected start month through last month. Months that already have an invoice will be skipped.
                                                    </p>
                                                    <div className="d-flex align-items-end gap-3">
                                                        <div>
                                                            <label className="form-label fw-bold small">Backfill Start Month</label>
                                                            <input
                                                                type="month"
                                                                className="form-control"
                                                                value={backfillMonth}
                                                                onChange={(e) => setBackfillMonth(e.target.value)}
                                                            />
                                                        </div>
                                                        <button
                                                            className="btn btn-warning"
                                                            onClick={handleEnableBackfill}
                                                            disabled={savingBackfill || !backfillMonth}
                                                        >
                                                            {savingBackfill ? 'Saving...' : 'Enable Backfill'}
                                                        </button>
                                                    </div>
                                                    <div className="form-check mt-3">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            id="backfillSendEmails"
                                                            checked={backfillSendEmails}
                                                            onChange={(e) => setBackfillSendEmails(e.target.checked)}
                                                        />
                                                        <label className="form-check-label small" htmlFor="backfillSendEmails">
                                                            Send email notifications for backfill invoices
                                                        </label>
                                                        <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                            When off, the customer will not receive emails for backfilled months. The current month's invoice email will always be sent.
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                                </>
                                )}
                            </div>
                        )}

                        {/* ===== INVOICES TAB ===== */}
                        {activeTab === 'invoices' && (
                            <div>
                                {loadingInvoices ? (
                                    <div className="text-center py-5">
                                        <div className="spinner-border me-2" role="status"></div>
                                        <span>Loading invoices...</span>
                                    </div>
                                ) : invoices.length === 0 ? (
                                    <div className="alert alert-info">
                                        No invoices have been generated for this organization yet.
                                    </div>
                                ) : (
                                    <div>
                                        {/* Summary Bar */}
                                        <div className="d-flex gap-3 mb-3">
                                            <span className="badge bg-light text-dark border px-3 py-2">
                                                Total: {invoices.length} invoice{invoices.length !== 1 ? 's' : ''}
                                            </span>
                                            {unpaidInvoices.length > 0 && (
                                                <span className="badge bg-danger px-3 py-2">
                                                    {unpaidInvoices.length} unpaid — ${unpaidTotal.toFixed(2)}
                                                </span>
                                            )}
                                            <button className="btn btn-sm btn-outline-secondary ms-auto" onClick={fetchInvoices}>
                                                Refresh
                                            </button>
                                        </div>

                                        {/* Invoice Table */}
                                        <div className="table-responsive">
                                            <table className="table table-sm table-hover">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>Month</th>
                                                        <th>Loans</th>
                                                        <th>Rate</th>
                                                        <th>Amount</th>
                                                        <th>Mode</th>
                                                        <th>Status</th>
                                                        <th>Created</th>
                                                        <th>Paid</th>
                                                        <th>Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {invoices.map((inv) => {
                                                        const statusStyle = INVOICE_STATUS_STYLES[inv.status] || { badge: 'bg-secondary', label: inv.status };
                                                        const isUnpaid = ['open', 'payment_failed', 'uncollectible'].includes(inv.status);

                                                        return (
                                                            <tr key={inv.id} className={isUnpaid ? 'table-warning' : ''}>
                                                                <td><strong>{inv.monthKey}</strong></td>
                                                                <td>{inv.loanCount}</td>
                                                                <td>${inv.pricePerLoan?.toFixed(2)}</td>
                                                                <td><strong>${inv.totalAmount?.toFixed(2)}</strong></td>
                                                                <td>
                                                                    <small>{inv.billingMode === 'auto_charge' ? 'Auto' : 'Invoice'}</small>
                                                                </td>
                                                                <td>
                                                                    <span className={`badge ${statusStyle.badge}`}>
                                                                        {statusStyle.label}
                                                                    </span>
                                                                    {inv.manuallyMarkedPaid && (
                                                                        <span className="badge bg-info ms-1" title="Manually marked paid by admin">Manual</span>
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    <small>{inv.createdAt ? new Date(inv.createdAt).toLocaleDateString() : '—'}</small>
                                                                </td>
                                                                <td>
                                                                    <small>{inv.paidAt ? new Date(inv.paidAt).toLocaleDateString() : '—'}</small>
                                                                </td>
                                                                <td>
                                                                    <div className="d-flex gap-1">
                                                                        {inv.invoiceUrl && (
                                                                            <a
                                                                                href={inv.invoiceUrl}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                className="btn btn-sm btn-outline-primary"
                                                                                title="View in Stripe"
                                                                            >
                                                                                <FaExternalLinkAlt />
                                                                            </a>
                                                                        )}
                                                                        {inv.invoicePdf && (
                                                                            <a
                                                                                href={inv.invoicePdf}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                className="btn btn-sm btn-outline-secondary"
                                                                                title="Download PDF"
                                                                            >
                                                                                <FaFilePdf />
                                                                            </a>
                                                                        )}
                                                                        {isUnpaid && (
                                                                            <>
                                                                                <button
                                                                                    className="btn btn-sm btn-outline-success"
                                                                                    onClick={(e) => { e.stopPropagation(); handleMarkPaid(inv); }}
                                                                                    disabled={!!actionInProgress}
                                                                                    title="Mark as Paid"
                                                                                >
                                                                                    {actionInProgress === 'markpaid-' + inv.id ? '...' : 'Paid'}
                                                                                </button>
                                                                                <button
                                                                                    className="btn btn-sm btn-outline-danger"
                                                                                    onClick={(e) => { e.stopPropagation(); handleForgiveInvoice(inv); }}
                                                                                    disabled={!!actionInProgress}
                                                                                    title="Forgive Invoice"
                                                                                >
                                                                                    {actionInProgress === 'forgive-' + inv.id ? '...' : 'Forgive'}
                                                                                </button>
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="modal-footer">
                        {isActivation ? (
                            <>
                                <button className="btn btn-outline-secondary" onClick={onClose} disabled={isActivating || savingConfig}>
                                    Cancel
                                </button>
                                <button className="btn btn-success" onClick={handleActivate} disabled={isActivating || savingConfig}>
                                    {isActivating || savingConfig ? 'Updating...' : 'Set to Enterprise'}
                                </button>
                            </>
                        ) : (
                            <button className="btn btn-secondary" onClick={onClose}>
                                Close
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EnterpriseBillingDialog;
